import { useEffect, useState } from 'react';
import { ActionIcon, Flex, Input, Text } from '@mantine/core';
import { TbCheck, TbPencil, TbX } from 'react-icons/tb';
import toast from 'react-hot-toast';
import { useGetWorkflowById, useUpdateWorkflow } from '../../services/workflow.services';

// Sanitizes project-name input the same way the legacy Funds-page editor did
// (alphanumeric + a small whitelist of punctuation). Keeps backend validation
// in sync with what the input lets through.
const sanitize = value => value.replace(/[^a-zA-Z0-9 ~!@#$%^&*()_+=-]/g, '');

/**
 * Inline editable project name for the workflow toolbar.
 *
 * Reads the current workflow via `useGetWorkflowById`, then renders the name.
 * Editing is gated to scratch periods (`template_type === 'sratch'`) — for
 * automation/from-template periods the name renders as plain text without a
 * pencil affordance, matching the rule that only initial-setup workflows
 * support rename.
 *
 * Mount via the workflow chrome's `toolbarLeftContent` slot:
 *
 *   setLabels({ toolbarLeftContent: <EditableProjectName workflowId={...} period={...} /> })
 */
export const EditableProjectName = ({ workflowId, period }) => {
  const { data: project, refetch: refetchWorkflow } = useGetWorkflowById({
    id: workflowId,
    queryParams: { enabled: !!workflowId }
  });

  const updateProject = useUpdateWorkflow({});

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Reset draft whenever editing opens or the resolved name changes.
  useEffect(() => {
    if (isEditing) setDraft(project?.name ?? '');
  }, [isEditing, project?.name]);

  // Only initial-setup (scratch) workflows are renameable. Backend stores
  // 'sratch' — typo preserved by API contract.
  const isScratch = period?.template_type === 'sratch';

  const onOpen = () => {
    setDraft(project?.name ?? '');
    setIsEditing(true);
  };

  const onCancel = () => setIsEditing(false);

  const onSubmit = e => {
    e?.preventDefault?.();
    if (!draft.trim()) return;
    updateProject.mutate(
      { id: project?.id, data: { name: draft.trim() } },
      {
        onSuccess: () => {
          setIsEditing(false);
          refetchWorkflow();
        },
        onError: () => {
          toast.error(
            'Something went wrong while changing the project name. Please make sure you have the right permissions'
          );
        }
      }
    );
  };

  if (!project?.name) return null;

  if (!isEditing) {
    return (
      <Flex align="center" gap={6}>
        <Text fz="13px" fw={500} c="gray.8">
          {project.name}
        </Text>
        {isScratch && (
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            onClick={onOpen}
            aria-label="Edit project name"
          >
            <TbPencil size={14} />
          </ActionIcon>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      component="form"
      align="center"
      gap={6}
      onSubmit={onSubmit}
      data-testid="editable-project-name-form"
    >
      <Input
        autoFocus
        required
        size="xs"
        w={220}
        value={draft}
        onChange={e => setDraft(sanitize(e.target.value))}
        aria-label="Project name"
      />
      <ActionIcon
        type="submit"
        variant="filled"
        size="sm"
        color="blue"
        loading={updateProject.isLoading}
        aria-label="Save project name"
      >
        <TbCheck size={14} />
      </ActionIcon>
      <ActionIcon
        type="button"
        variant="subtle"
        size="sm"
        color="gray"
        onClick={onCancel}
        aria-label="Cancel project name edit"
      >
        <TbX size={14} />
      </ActionIcon>
    </Flex>
  );
};

export default EditableProjectName;
